import { useState, useEffect, useMemo } from 'react'
import { useTodaysBookings } from '@/hooks/useTodaysBookings'
import type { Booking } from '@/types'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Clock, Calendar } from 'lucide-react'

const START_HOUR = 6 // 6 AM
const END_HOUR = 22 // 10 PM
const TOTAL_HOURS = END_HOUR - START_HOUR // 16 hours
const TOTAL_MINUTES = TOTAL_HOURS * 60 // 960 minutes

function formatTime(hour: number): string {
  const period = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour
  return `${displayHour} ${period}`
}

function parseTimeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number)
  return (hours - START_HOUR) * 60 + minutes
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  })
}

interface BookingBlockProps {
  booking: Booking
  position: number
  height: number
  isPast: boolean
  isNextUpcoming: boolean
}

function BookingBlock({ booking, position, height, isPast, isNextUpcoming }: BookingBlockProps) {
  const bgClass = isPast
    ? 'bg-gray-400 dark:bg-gray-600 opacity-50'
    : isNextUpcoming
      ? 'bg-pink-500 text-white'
      : 'bg-emerald-500 text-white'

  const serviceName = booking.services?.[0]?.name || 'Appointment'

  return (
    <div
      className={`absolute left-2 right-2 rounded-md px-2 py-1 text-xs overflow-hidden ${bgClass}`}
      style={{
        top: `${position}%`,
        height: `${height}%`,
        minHeight: height > 5 ? '24px' : '20px',
      }}
    >
      <div className="font-medium truncate">{booking.customer_name}</div>
      <div className="truncate opacity-90">{serviceName}</div>
      <div className="text-xs opacity-75">
        {booking.booking_time} ({booking.duration_minutes} min)
      </div>
    </div>
  )
}

function CurrentTimeMarker({ currentMinutes }: { currentMinutes: number }) {
  const position = (currentMinutes / TOTAL_MINUTES) * 100

  return (
    <div
      className="absolute left-0 right-0 flex items-center z-10 pointer-events-none"
      style={{ top: `${position}%` }}
    >
      <div className="w-2 h-2 rounded-full bg-pink-500 -ml-1" />
      <div className="flex-1 h-0.5 bg-pink-500" />
      <div className="bg-pink-500 text-white text-xs px-1.5 py-0.5 rounded -mr-1">
        Now
      </div>
    </div>
  )
}

function TimeSlotSkeleton() {
  return (
    <div className="animate-pulse space-y-4 p-4">
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
      <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded" />
      <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded" />
      <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded" />
      <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded" />
    </div>
  )
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <Calendar className="w-12 h-12 text-gray-400 mb-3" />
      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
        No appointments today
      </h3>
      <p className="text-sm text-gray-500 mt-1">
        Your schedule is clear for today
      </p>
    </div>
  )
}

export function DayCalendarView() {
  const { bookings, isLoading } = useTodaysBookings()
  const [currentTime, setCurrentTime] = useState(new Date())

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date())
    }, 60000) // 60 seconds

    return () => clearInterval(interval)
  }, [])

  // Calculate current time position in minutes from start hour
  const currentMinutes = useMemo(() => {
    const hours = currentTime.getHours()
    const minutes = currentTime.getMinutes()
    return (hours - START_HOUR) * 60 + minutes
  }, [currentTime])

  // Determine booking statuses
  const { pastBookings, nextUpcomingBooking } = useMemo(() => {
    if (!bookings.length) {
      return { pastBookings: [], nextUpcomingBooking: null }
    }

    const now = currentTime.getTime()
    const sortedBookings = [...bookings].sort((a, b) => {
      const timeA = new Date(`${a.booking_date}T${a.booking_time}`).getTime()
      const timeB = new Date(`${b.booking_date}T${b.booking_time}`).getTime()
      return timeA - timeB
    })

    let nextUpcoming: Booking | null = null
    const past: Booking[] = []

    for (const booking of sortedBookings) {
      const bookingStart = new Date(`${booking.booking_date}T${booking.booking_time}`).getTime()
      const bookingEnd = bookingStart + booking.duration_minutes * 60 * 1000

      if (bookingEnd < now) {
        past.push(booking)
      } else if (bookingStart <= now && bookingEnd >= now) {
        // Currently in progress - this is the next upcoming
        nextUpcoming = booking
      } else if (!nextUpcoming) {
        // First booking that hasn't started yet
        nextUpcoming = booking
      }
    }

    return {
      pastBookings: past,
      nextUpcomingBooking: nextUpcoming,
    }
  }, [bookings, currentTime])

  // Generate time slots for the y-axis
  const timeSlots = useMemo(() => {
    return Array.from({ length: TOTAL_HOURS + 1 }, (_, i) => START_HOUR + i)
  }, [])

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-48 animate-pulse" />
        </CardHeader>
        <CardContent>
          <TimeSlotSkeleton />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-500" />
            <span>Today's Schedule</span>
          </div>
          <span className="text-sm font-normal text-gray-500">
            {formatDate(new Date())}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {bookings.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="relative">
            {/* Time slots grid */}
            <div
              className="relative border-l border-gray-200 dark:border-gray-700"
              style={{ height: `${TOTAL_HOURS * 60}px` }}
            >
              {/* Hour grid lines */}
              {timeSlots.map((hour) => (
                <div
                  key={hour}
                  className="absolute left-0 right-0 border-t border-gray-100 dark:border-gray-800"
                  style={{ top: `${((hour - START_HOUR) / TOTAL_HOURS) * 100}%` }}
                />
              ))}

              {/* Current time marker */}
              {currentMinutes >= 0 && currentMinutes <= TOTAL_MINUTES && (
                <CurrentTimeMarker currentMinutes={currentMinutes} />
              )}

              {/* Booking blocks */}
              {bookings.map((booking) => {
                const topMinutes = parseTimeToMinutes(booking.booking_time)
                const position = (topMinutes / TOTAL_MINUTES) * 100
                const height = (booking.duration_minutes / TOTAL_MINUTES) * 100
                const isPast = pastBookings.includes(booking)
                const isNextUpcoming = nextUpcomingBooking?.id === booking.id

                return (
                  <BookingBlock
                    key={booking.id}
                    booking={booking}
                    position={position}
                    height={height}
                    isPast={isPast}
                    isNextUpcoming={isNextUpcoming}
                  />
                )
              })}
            </div>

            {/* Time labels */}
            <div className="flex mt-2 text-xs text-gray-500">
              <div className="w-12 text-right pr-2">
                {formatTime(START_HOUR)}
              </div>
              <div className="flex-1 text-center">
                {formatTime(END_HOUR)}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
